/**
 * Phase 32 Plan 32-2 — Engineering Metrics comparison view.
 *
 * Surface inventory (UI-SPEC §195-204):
 *   /reports/engineering            ManagerRoute → THIS PAGE
 *
 * Layout:
 *   1. Header (H1 "Engineering Metrics" + subtitle)
 *   2. Window selector (UI-SPEC §211 verbatim)
 *   3. Project filter dropdown (sourced from useProjects())
 *   4. Top-line "Automation share" card (UI-SPEC §150)
 *   5. Leaderboard bar chart — clicking a bar navigates to the drill-down
 *
 * URL search params bind window + projectId so the view is shareable
 * (UI-SPEC §227).
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ReactElement } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../../components/common/Spinner';
import { LeaderboardBarChart } from '../../components/engineering/LeaderboardBarChart';
import {
  useAutomationShare,
  useLeaderboard,
} from '../../hooks/useEngineeringMetrics';
import { useProjects } from '../../hooks/useProjects';
import type { Window } from '../../services/api/engineeringMetrics';

const numberFmt = new Intl.NumberFormat();

const WINDOW_OPTIONS: Window[] = ['7d', '30d', '13w', 'all'];
const WINDOW_LABEL: Record<Window, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '13w': '13 weeks',
  all: 'All time',
};

function isWindow(s: string | null): s is Window {
  return s === '7d' || s === '30d' || s === '13w' || s === 'all';
}

export default function EngineeringMetricsPage(): ReactElement {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const windowKey: Window = isWindow(params.get('window')) ? (params.get('window') as Window) : '30d';
  const rawProjectId = params.get('projectId');
  const projectId = rawProjectId && /^\d+$/.test(rawProjectId) ? Number(rawProjectId) : undefined;

  const setWindow = (w: Window) => {
    params.set('window', w);
    setParams(params, { replace: true });
  };
  const setProjectId = (pid: number | undefined) => {
    if (pid === undefined) params.delete('projectId');
    else params.set('projectId', String(pid));
    setParams(params, { replace: true });
  };

  const projectsQ = useProjects();
  const leaderboardQ = useLeaderboard({ window: windowKey, projectId });
  const automationQ = useAutomationShare(windowKey);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Engineering Metrics</h1>
        <p className="mt-1 text-sm text-gray-600">
          Per-engineer commit activity, AI-assisted share, and automation noise across
          linked GitHub repositories. Data refreshes daily at 02:00 UTC.
        </p>
      </div>

      {/* Filter bar: window + project */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              aria-pressed={windowKey === w}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                windowKey === w
                  ? 'bg-white text-primary-700 shadow-sm font-semibold'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {WINDOW_LABEL[w]}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Project:</span>
          <select
            value={projectId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setProjectId(v ? Number(v) : undefined);
            }}
            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">All projects</option>
            {(projectsQ.data ?? [])
              .filter((p) => p.state === 'in_progress')
              .slice()
              .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      {/* Top-line automation share */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase text-gray-400">Automation share</h2>
          {automationQ.isLoading && <Spinner size="sm" />}
        </div>
        {automationQ.data && (
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-gray-900">
              {Math.round(automationQ.data.share * 100)}%
            </span>
            <span className="text-sm text-gray-500">
              {automationQ.data.automationCount} of {automationQ.data.totalCount} commits this
              window were classified as automation (bots, scheduled deploys, dependabot).
              Excluded from per-engineer counts.
            </span>
          </div>
        )}
        {automationQ.error && (
          <p className="mt-2 text-sm text-red-700">
            Could not load automation share: {(automationQ.error as Error).message}
          </p>
        )}
      </div>

      {/* Leaderboard */}
      <div>
        <h2 className="text-sm font-semibold uppercase text-gray-400 mb-2">
          Engineers — ranked by commit count ({WINDOW_LABEL[windowKey]})
        </h2>
        {leaderboardQ.isLoading && (
          <div className="flex items-center justify-center h-64">
            <Spinner />
          </div>
        )}
        {leaderboardQ.error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            Could not load leaderboard: {(leaderboardQ.error as Error).message}
          </div>
        )}
        {leaderboardQ.data && (
          <>
            <LeaderboardBarChart
              rows={leaderboardQ.data}
              onSelect={(uid) => {
                const sp = new URLSearchParams();
                sp.set('window', windowKey);
                if (projectId) sp.set('projectId', String(projectId));
                navigate(`/reports/engineering/users/${uid}?${sp.toString()}`);
              }}
            />
            {/* Line-stats table — new columns per quick-260513-92r */}
            <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Engineer
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Commits
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <span>+/&#x2212; lines</span>
                        <InformationCircleIcon
                          className="h-4 w-4 text-gray-400"
                          title="Includes lockfiles &amp; generated files; not a productivity metric"
                        />
                      </span>
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Files
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leaderboardQ.data.map((row) => {
                    const sp = new URLSearchParams();
                    sp.set('window', windowKey);
                    if (projectId) sp.set('projectId', String(projectId));
                    const href = `/reports/engineering/users/${row.userId}?${sp.toString()}`;
                    return (
                      <tr key={row.userId}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          <button
                            type="button"
                            onClick={() => navigate(href)}
                            className="text-primary-700 hover:text-primary-800 hover:underline text-left"
                          >
                            {row.userName}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-right text-sm text-gray-700">
                          {numberFmt.format(row.total)}
                        </td>
                        <td className="px-4 py-2 text-right text-sm">
                          <span className="text-green-700">+{numberFmt.format(row.additions.total)}</span>
                          {' / '}
                          <span className="text-red-700">&#x2212;{numberFmt.format(row.deletions.total)}</span>
                        </td>
                        <td className="px-4 py-2 text-right text-sm text-gray-700">
                          {numberFmt.format(row.filesChanged.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
